import { Helmet } from "react-helmet-async";
import { useLang } from "@/components/LanguageContext";
import { pathFor } from "@/data/pages";

const SITE = "https://abs-solutions.com.au";

export default function SEO({ pageId, title, description, image, schema, pathOverride }) {
  const { lang } = useLang();
  const origin = typeof window !== "undefined" ? window.location.origin : SITE;
  const path = pathOverride || (pageId ? pathFor(pageId, lang) : "/");
  const url = `${origin}${path}`;
  const altLang = lang === "en" ? "zh" : "en";
  const altPath = pageId ? pathFor(pageId, altLang) : "/";
  const altUrl = `${origin}${altPath}`;

  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    name: "Absolute Business Services (ABS) Solutions",
    image: image,
    url,
    telephone: "+61 3 9826 1316",
    email: "info@abs-solutions.com.au",
    address: {
      "@type": "PostalAddress",
      streetAddress: "205 Canterbury Road",
      addressLocality: "Canterbury",
      addressRegion: "VIC",
      postalCode: "3126",
      addressCountry: "AU",
    },
  };

  return (
    <>
      <Helmet htmlAttributes={{ lang: lang === "zh" ? "zh" : "en" }}>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        {image && <meta property="og:image" content={image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {image && <meta name="twitter:image" content={image} />}
        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang={lang === "zh" ? "zh-CN" : "en-AU"} href={url} />
        <link rel="alternate" hrefLang={altLang === "zh" ? "zh-CN" : "en-AU"} href={altUrl} />
        <link rel="alternate" hrefLang="x-default" href={`${origin}/`} />
        <script type="application/ld+json">{JSON.stringify(schema || baseSchema)}</script>
      </Helmet>
      {/*
        Canonical SEO snapshot for the build-time prerender. React-helmet-async
        can leave stale duplicate <title>/<meta> tags in the DOM when the
        language switches during first render, so we also expose the current,
        committed SEO values as a deterministic JSON blob that scripts/prerender.mjs
        reads to rebuild <head> reliably. This runs on the client too; it is a
        tiny inert <script type="application/json"> element and does not execute.
      */}
      <script
        type="application/json"
        data-seo-snapshot="true"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            lang: lang === "zh" ? "zh" : "en",
            title,
            description,
            image: image || null,
            url,
            altUrl,
            altHreflang: altLang === "zh" ? "zh-CN" : "en-AU",
            selfHreflang: lang === "zh" ? "zh-CN" : "en-AU",
            xDefault: `${origin}/`,
            jsonLd: schema || baseSchema,
          }),
        }}
      />
    </>
  );
}
